import { Lead } from '../services/leadService';

export type LeadStatusColor = 'success' | 'warning' | 'info' | 'default';

export interface LeadDisplayStatus {
  label: string;
  color: LeadStatusColor;
}

/**
 * How far along the referral is, which is not the same as its status column.
 *
 * Four steps, and the referral is somewhere on them:
 *
 *   Not sent   the questionnaire never went out - ours to fix
 *   Sent       WhatsApp accepted it
 *   Delivered  it reached a handset
 *   Completed  the person filled it in
 *
 * The status column knows only the first and the last of those. Everything in
 * between read as one "Waiting", which is how a referral sat for three days
 * behind a message that had gone to a number belonging to nobody: the screen
 * said it was waiting on the patient, and the patient had never heard from us.
 *
 * Sent and Delivered are different facts because WhatsApp accepts any
 * well-formed number whether or not anybody holds it. Both dates are already on
 * the lead, so this reads what is there rather than adding anything.
 *
 * Any other status keeps the title its tenant gave it - renaming somebody's own
 * configured status would be the app inventing meaning it was not given.
 */
/**
 * When a send first became something the system could record.
 *
 * questionnaire_sent_at arrived on 18/08/2026, alongside England sending the
 * WhatsApp itself. A referral opened before that could not have recorded a send
 * however well it went - the questionnaire went out in the Zoho era and nothing
 * wrote it down.
 *
 * It matters most for the South, whose migration back-dates created_at to the
 * referral's original opening date, some of them from 2025. Without this they
 * would all arrive wearing a "Not sent" that is simply false, on the one screen
 * that shows every district. Taken from the rule written on 01/09/2026 for the
 * Questionnaire Sent column, so that the column and the status cannot disagree.
 */
const SEND_RECORDED_FROM = new Date('2026-08-18T00:00:00Z').getTime();

function predatesSendTracking(createdAt?: string | null): boolean {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  return !Number.isNaN(t) && t < SEND_RECORDED_FROM;
}

export function leadDisplayStatus(
  lead: Pick<Lead, 'status' | 'created_at' | 'questionnaire_sent_at' | 'questionnaire_delivered_at'>,
  statusLabel: (key: string | null | undefined) => string,
): LeadDisplayStatus {
  if (lead.status === 'MATCHED') {
    return { label: 'Completed', color: 'success' };
  }
  if (lead.status === 'CREATED') {
    if (lead.questionnaire_delivered_at) {
      return { label: 'Delivered', color: 'info' };
    }
    if (lead.questionnaire_sent_at) {
      return { label: 'Sent', color: 'default' };
    }
    // Nothing recorded, and nothing could have been. Saying "Not sent" here
    // would turn every referral older than the column into a false alarm, on
    // the screen somebody is meant to work from.
    if (predatesSendTracking(lead.created_at)) {
      return { label: 'Sent', color: 'default' };
    }
    return { label: 'Not sent', color: 'warning' };
  }
  return { label: statusLabel(lead.status), color: 'default' };
}

/**
 * The same four steps, as filter options.
 *
 * Deliberately in this file and not beside the dropdown: a filter whose options
 * drift from the labels is worse than no filter - somebody picks "Delivered"
 * and gets rows the screen calls something else, and the list stops meaning
 * anything. Keeping both here means one edit, not two.
 *
 * The keys are what the server's stage_filter understands.
 */
export const LEAD_STAGE_OPTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'NOT_SENT', label: 'Not sent' },
  { key: 'SENT', label: 'Sent' },
  { key: 'DELIVERED', label: 'Delivered' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'NOT_INTERESTED', label: 'Not interested' },
];
